import { ChevronDown, ChevronRight, X } from "lucide-react";
import type React from "react";
import { useState } from "react";

interface OwnershipTierCardProps {
	tierNumber: number;
	summary: string;
	onRemove?: () => void;
	canRemove: boolean;
	children: React.ReactNode;
}

/**
 * Collapsible card for an ownership tier in Step 3.
 * Shows a summary line when collapsed (e.g. "Tier 1: ACME Corp — 45%").
 */
export function OwnershipTierCard({
	tierNumber,
	summary,
	onRemove,
	canRemove,
	children,
}: OwnershipTierCardProps) {
	const [collapsed, setCollapsed] = useState(false);

	return (
		<div className="mb-4 overflow-hidden rounded-md border-(--color-border) border-2">
			<div
				className="flex cursor-pointer select-none items-center justify-between bg-(--color-muted) px-4 py-3"
				onClick={() => setCollapsed((c) => !c)}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault();
						setCollapsed((c) => !c);
					}
				}}
				role="button"
				tabIndex={0}
				aria-expanded={!collapsed}
			>
				<div className="flex items-center gap-2">
					<span className="text-(--color-muted-foreground)">
						{collapsed ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
					</span>
					<strong>Owner {tierNumber}</strong>
					{summary && (
						<span className="text-(--color-muted-foreground) text-sm">
							— {summary}
						</span>
					)}
				</div>
				{canRemove && onRemove && (
					<button
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							onRemove();
						}}
						className="flex cursor-pointer items-center gap-1 border-none bg-transparent px-2 py-1 text-(--color-destructive)"
						aria-label={`Remove owner ${tierNumber}`}
					>
						<X size={16} />
						Remove
					</button>
				)}
			</div>
			{!collapsed && <div className="p-4">{children}</div>}
		</div>
	);
}
